// Create src/errorPage/DeveloperErrorOverlay.tsx
import React, { useState, useEffect } from "react";

interface ErrorInfo {
    error: Error;
    componentStack?: string | null;
    timestamp: Date;
    id: string;
}

interface ErrorDetail {
    error: Error;
    componentStack?: string | null;
}

const DeveloperErrorOverlay: React.FC = () => {
    const [errors, setErrors] = useState<ErrorInfo[]>([]);

    useEffect(() => {
        const handleError = (event: CustomEvent<ErrorDetail>) => {
            const newError: ErrorInfo = {
                error: event.detail.error,
                componentStack: event.detail.componentStack,
                timestamp: new Date(),
                id: Date.now().toString(),
            };

            setErrors((prev) => [newError, ...prev.slice(0, 4)]); // Keep only last 5 errors
        };

        window.addEventListener("dev-error", handleError as EventListener);

        return () => {
            window.removeEventListener("dev-error", handleError as EventListener);
        };
    }, []);

    const cleanStackTrace = (stackTrace: string): string => {
        return stackTrace
            .split("\n")
            .map((line) => {
                // Remove localhost URLs with any port number
                return line.replace(/http:\/\/localhost:\d+/g, "");
            })
            .join("\n");
    };

    const cleanComponentStack = (componentStack: string): string => {
        return componentStack
            .split("\n")
            .map((line) => {
                // Remove localhost URLs with any port number from component stack
                return line.replace(/http:\/\/localhost:\d+/g, "");
            })
            .join("\n");
    };

    const copyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            // Fallback: select text for manual copy
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
        }
    };

    if (errors.length === 0) return null;

    return (
        <div
            style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                zIndex: 9999,
                maxWidth: "1000px",
                fontFamily: "system-ui, -apple-system, sans-serif",
            }}
        >
            {errors.map((errorInfo) => {
                const cleanedStackTrace = errorInfo.error.stack ? cleanStackTrace(errorInfo.error.stack) : "";
                const cleanedComponentStack = errorInfo.componentStack ? cleanComponentStack(errorInfo.componentStack) : "";

                return (
                    <div
                        key={errorInfo.id}
                        style={{
                            backgroundColor: "#ff6b6b",
                            color: "white",
                            padding: "12px 16px",
                            marginBottom: "8px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                            fontSize: "16px",
                            cursor: "pointer",
                            animation: "slideIn 0.3s ease-out",
                            maxHeight: "800px",
                            overflow: "hidden",
                            transition: "max-height 0.3s ease",
                        }}
                        onClick={() => setErrors([])}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "4px",
                            }}
                        >
                            <div style={{ fontWeight: "bold" }}>🚨 {errorInfo.error.name || "Error"} (development)</div>
                            <div
                                style={{
                                    fontSize: "12px",
                                    opacity: 0.8,
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                }}
                            >
                                <span>{errorInfo.timestamp.toLocaleTimeString()}</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const fullError = [
                                            `Error: ${errorInfo.error.name || "Unknown"}`,
                                            `Message: ${errorInfo.error.message}`,
                                            `Timestamp: ${errorInfo.timestamp.toISOString()}`,
                                            cleanedComponentStack ? `\nComponent Stack:\n${cleanedComponentStack}` : "",
                                            cleanedStackTrace ? `\nStack Trace:\n${cleanedStackTrace}` : "",
                                        ]
                                            .filter(Boolean)
                                            .join("\n");

                                        copyToClipboard(fullError, "Full error details");
                                    }}
                                    style={{
                                        background: "rgba(255,255,255,0.2)",
                                        border: "none",
                                        color: "white",
                                        padding: "6px 12px",
                                        fontSize: "12px",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontWeight: "500",
                                    }}
                                    title="Copy all error details to clipboard"
                                >
                                    📋 Copy All
                                </button>
                            </div>
                        </div>

                        <div
                            style={{
                                fontSize: "12px",
                                opacity: 0.9,
                                marginBottom: "8px",
                            }}
                        >
                            {errorInfo.error.message}
                        </div>

                        <div
                            style={{
                                borderTop: "1px solid rgba(255,255,255,0.3)",
                                paddingTop: "8px",
                                marginTop: "8px",
                            }}
                        >
                            {cleanedComponentStack && (
                                <div style={{ marginBottom: "12px" }}>
                                    <div
                                        style={{
                                            fontWeight: "bold",
                                            fontSize: "11px",
                                            marginBottom: "4px",
                                            opacity: 0.8,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <span>COMPONENT STACK:</span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyToClipboard(cleanedComponentStack, "Component stack");
                                            }}
                                            style={{
                                                background: "rgba(255,255,255,0.2)",
                                                border: "none",
                                                color: "white",
                                                padding: "4px 8px",
                                                fontSize: "11px",
                                                borderRadius: "3px",
                                                cursor: "pointer",
                                                fontWeight: "500",
                                            }}
                                            title="Copy component stack to clipboard"
                                        >
                                            📋 Copy
                                        </button>
                                    </div>
                                    <pre
                                        style={{
                                            fontSize: "12px",
                                            backgroundColor: "rgba(0,0,0,0.2)",
                                            padding: "6px",
                                            borderRadius: "4px",
                                            margin: 0,
                                            whiteSpace: "pre-wrap",
                                            maxHeight: "100px",
                                            overflow: "auto",
                                            userSelect: "text",
                                            cursor: "text",
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {cleanedComponentStack}
                                    </pre>
                                </div>
                            )}

                            {cleanedStackTrace && (
                                <div>
                                    <div
                                        style={{
                                            fontWeight: "bold",
                                            fontSize: "12px",
                                            marginBottom: "4px",
                                            opacity: 0.8,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <span>STACK TRACE:</span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyToClipboard(cleanedStackTrace, "Stack trace");
                                            }}
                                            style={{
                                                background: "rgba(255,255,255,0.2)",
                                                border: "none",
                                                color: "white",
                                                padding: "4px 8px",
                                                fontSize: "11px",
                                                borderRadius: "3px",
                                                cursor: "pointer",
                                                fontWeight: "500",
                                            }}
                                            title="Copy stack trace to clipboard"
                                        >
                                            📋 Copy
                                        </button>
                                    </div>
                                    <pre
                                        style={{
                                            fontSize: "12px",
                                            backgroundColor: "rgba(0,0,0,0.2)",
                                            padding: "6px",
                                            borderRadius: "4px",
                                            margin: 0,
                                            whiteSpace: "pre-wrap",
                                            maxHeight: "150px",
                                            overflow: "auto",
                                            userSelect: "text",
                                            cursor: "text",
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {cleanedStackTrace}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            <style>
                {`
                    @keyframes slideIn {
                        from { transform: translateX(100%); opacity: 0; }
                        to { transform: translateX(0); opacity: 1; }
                    }
                `}
            </style>
        </div>
    );
};

export default DeveloperErrorOverlay;
